import { OhsTabKeyTypes } from 'global-components/tab/OhsTabModels';

type BooleanString = '1' | '0';

export enum ParamTypes {
  Tab = 'tab',

  Archived = 'archived',
  Confidential = 'confidential',
  ShowSignedOff = 'showSignedOff',
  Sort = 'sort',
  Severities = 'severities',
  SeveritiesHash = 'severitiesHash',
  Categories = 'categories',
  CategoriesHash = 'categoriesHash',
  SubCategories = 'subcategories',
  SubCategoriesHash = 'subcategoriesHash',
  Workplaces = 'workplaces',
  WorkplacesHash = 'workplacesHash',
  Modules = 'modules',
  ModulesHash = 'modulesHash',
  DateDue = 'dateDue',
  DateDueType = 'dateDueType',
  DateCompleted = 'dateCompleted',
  DateSignedoff = 'dateSignedoff',
  DateCreated = 'dateCreated',
  DateIdentified = 'dateIdentified',
  DateOccurred = 'dateOccurred',
  DateIssued = 'dateIssued',
  DateExpiry = 'dateExpiry',
  AllocatedToOrg = 'allocatedToOrg',
  AllocatedToWorkplace = 'allocatedToWorkplace',
  ViewPreset = 'viewPreset',
  WorkplaceOwned = 'workplaceOwned',
  NotAllocated = 'notAllocated',
  PropOrEnvDamage = 'propOrEnvDamage',
  InProgress = 'inProgress',

  // Search
  SearchKey = 'searchKey',
  SubstringSearch = 'substringSearch',
  SkipOrgRecords = 'skipOrgRecords',
  SkipAllocatedRecords = 'skipAllocatedRecords',
  Status = 'status',
  StatusHash = 'statusHash',

  // Document
  FolderView = 'folderView',
  ReviewScheduled = 'reviewScheduled',
  Copied = 'copied',
  CopiedVersionUpdated = 'copiedVersionUpdated',
  NotCopied = 'notCopied',
  Category = 'category',
  Subcategory = 'subcategory',
  TemplateTypes = 'templateTypes',

  Leave = 'leave',
  IncludeTierNull = 'includeTierNull',

  Page = 'page',
  Next = 'next',
}

// needed for proper handling of case issues in url params
export const ohsListParamKeys = Object.values(ParamTypes) as (keyof OhsListParams)[];

// type for params directly taken from url
export interface OhsListParams {
  [ParamTypes.Tab]?: string;

  [ParamTypes.Archived]?: BooleanString;
  [ParamTypes.Confidential]?: BooleanString;
  [ParamTypes.ShowSignedOff]?: BooleanString;
  [ParamTypes.Sort]?: string;
  [ParamTypes.Severities]?: string;
  [ParamTypes.SeveritiesHash]?: string;
  [ParamTypes.Categories]?: string;
  [ParamTypes.CategoriesHash]?: string;
  [ParamTypes.SubCategories]?: string;
  [ParamTypes.SubCategoriesHash]?: string;
  [ParamTypes.Workplaces]?: string; // [1,0,1]
  [ParamTypes.WorkplacesHash]?: string;
  [ParamTypes.Modules]?: string;
  [ParamTypes.ModulesHash]?: string;
  [ParamTypes.DateDue]?: string;
  [ParamTypes.DateDueType]?: string;
  [ParamTypes.DateCompleted]?: string;
  [ParamTypes.DateSignedoff]?: string;
  [ParamTypes.DateCreated]?: string;
  [ParamTypes.DateIdentified]?: string;
  [ParamTypes.DateOccurred]?: string;
  [ParamTypes.DateIssued]?: string;
  [ParamTypes.DateExpiry]?: string;
  [ParamTypes.Status]?: string;
  [ParamTypes.StatusHash]?: string;
  [ParamTypes.AllocatedToOrg]?: BooleanString;
  [ParamTypes.AllocatedToWorkplace]?: BooleanString;
  [ParamTypes.ViewPreset]?: string;
  [ParamTypes.WorkplaceOwned]?: BooleanString;
  [ParamTypes.NotAllocated]?: BooleanString;
  [ParamTypes.PropOrEnvDamage]?: BooleanString;
  [ParamTypes.InProgress]?: BooleanString;

  // Search
  [ParamTypes.SearchKey]?: string;
  [ParamTypes.SubstringSearch]?: BooleanString;
  [ParamTypes.SkipOrgRecords]?: BooleanString;
  [ParamTypes.SkipAllocatedRecords]?: BooleanString;

  // Document
  [ParamTypes.FolderView]?: BooleanString;
  [ParamTypes.ReviewScheduled]?: string;
  [ParamTypes.Copied]?: string;
  [ParamTypes.CopiedVersionUpdated]?: string;
  [ParamTypes.NotCopied]?: string;
  [ParamTypes.Category]?: string;
  [ParamTypes.Subcategory]?: string;
  [ParamTypes.TemplateTypes]?: string;

  [ParamTypes.Leave]?: BooleanString;
  [ParamTypes.IncludeTierNull]?: BooleanString;
  [ParamTypes.Page]?: string;
  [ParamTypes.Next]?: string;
}

// type for params formatted from url
export interface OhsFormattedParamList {
  [ParamTypes.Tab]: OhsTabKeyTypes;

  [ParamTypes.Archived]: boolean;
  [ParamTypes.Confidential]: boolean;
  [ParamTypes.ShowSignedOff]: boolean;
  [ParamTypes.PropOrEnvDamage]?: boolean;
  [ParamTypes.Sort]: { order: number; key: string };
  [ParamTypes.Severities]: string[];
  [ParamTypes.Categories]: string[];
  [ParamTypes.SubCategories]: string[];
  [ParamTypes.Status]: string[];
  [ParamTypes.Workplaces]: string[];
  [ParamTypes.Modules]: string[];
  [ParamTypes.DateDue]: { start: string; end: string } | string;
  [ParamTypes.DateDueType]: string;
  [ParamTypes.DateCompleted]: { start: string | null; end: string | null };
  [ParamTypes.DateSignedoff]: { start: string | null; end: string | null };
  [ParamTypes.DateCreated]: { start: string | null; end: string | null };
  [ParamTypes.DateOccurred]: { start: string | null; end: string | null };
  [ParamTypes.DateIdentified]: { start: string | null; end: string | null };
  [ParamTypes.DateIssued]: { start: string | null; end: string | null };
  [ParamTypes.DateExpiry]: { start: string | null; end: string | null };
  [ParamTypes.AllocatedToOrg]: boolean;
  [ParamTypes.AllocatedToWorkplace]: boolean;
  [ParamTypes.ViewPreset]: string;
  [ParamTypes.WorkplaceOwned]: boolean;
  [ParamTypes.NotAllocated]: boolean;
  [ParamTypes.InProgress]: boolean;

  // Search
  [ParamTypes.SearchKey]: string;
  hasSearch: boolean;
  [ParamTypes.SubstringSearch]: boolean;
  [ParamTypes.SkipOrgRecords]: boolean;
  [ParamTypes.SkipAllocatedRecords]: boolean;

  // Document
  [ParamTypes.FolderView]: boolean;
  [ParamTypes.ReviewScheduled]: boolean;
  [ParamTypes.Copied]?: boolean;
  [ParamTypes.CopiedVersionUpdated]?: boolean;
  [ParamTypes.NotCopied]?: boolean;
  [ParamTypes.Category]: string;
  [ParamTypes.Subcategory]: string;
  [ParamTypes.TemplateTypes]: string[];

  [ParamTypes.Leave]: boolean;
  [ParamTypes.IncludeTierNull]: boolean;

  [ParamTypes.Page]: number;
  [ParamTypes.Next]: string;
}
